import { useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';

const SettingsTable = ({ data, onEditType, onDeleteType, pageSize, onPageSizeChange }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Format type name for display
  const formatTypeName = (key) => {
    if (!key) return '';
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Convert object data to array for pagination
  const configEntries = Object.entries(data || {});
  const totalPages = Math.ceil(configEntries.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = configEntries.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white">
        <thead>
          <tr className="bg-gray-100 border-b border-gray-200">
            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
              Configuration Type
            </th>
            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
              Description
            </th>
            <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
              Items Count
            </th>
            <th className="py-3 px-4 text-center text-sm font-semibold text-gray-700 w-20">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.length === 0 ? (
            <tr>
              <td colSpan="4" className="py-8 text-center text-gray-500">
                No configuration data available
              </td>
            </tr>
          ) : (
            paginatedData.map(([key, value]) => (
              <tr
                key={key}
                className="border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="py-3 px-4">
                  <button
                    onClick={() => onEditType(key)}
                    className="font-medium text-secondary-700 hover:text-secondary-900 hover:underline cursor-pointer text-left"
                  >
                    {key}
                  </button>
                </td>
                <td className="py-3 px-4 text-gray-600">
                  {value?.description || '-'}
                </td>
                <td className="py-3 px-4 text-gray-600">
                  {typeof value === 'object' && value !== null
                    ? Object.values(value).reduce((sum, v) => sum + (Array.isArray(v) ? v.length : 0), 0)
                    : 0}
                </td>
                <td className="py-3 px-4 text-center">
                  <button
                    onClick={() => onDeleteType(key)}
                    className="text-red-400 hover:text-red-600 transition p-1"
                    title={`Delete ${formatTypeName(key)}`}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="flex justify-between items-center p-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            Showing {configEntries.length > 0 ? startIndex + 1 : 0} to{' '}
            {Math.min(startIndex + pageSize, configEntries.length)} of {configEntries.length} entries
          </span>
          {totalPages > 1 && (
            <div className="flex gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === page
                      ? 'bg-secondary-700 text-white border-secondary-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsTable;
